import { FamilyIteration } from 'models/projectElementNode'
import { ProjectElementForImport } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { ActionBarStoreType, FileType } from './types/types'

export const useActionBarStore = create<ActionBarStoreType>()((set) => ({
    /** initial state */
    isPDFExportLoading: false,
    isWordExportLoading: false,
    isSupportingDocsLoading: false,
    pdfExportUrl: '',
    wordExportUrl: '',
    supportingDocsUrl: '',
    selectedOMVersion: undefined,
    omDropdownOptions: [],
    elementList: null,
    assemblyElementList: null,
    divisionElementList: null,
    sustainabilityElementList: null,
    /** actions/setters */
    actions: {
        setIsExportLoading: (fileType: FileType, isExportLoading: boolean) =>
            fileType === FileType.PDF
                ? set({ isPDFExportLoading: isExportLoading })
                : set({ isWordExportLoading: isExportLoading }),
        setIsSupportingDocsLoading: (isSupportingDocsLoading: boolean) =>
            set({ isSupportingDocsLoading }),
        setExportUrl: (fileType: string, exportUrl: string) =>
            fileType === 'pdf'
                ? set({ pdfExportUrl: exportUrl })
                : set({ wordExportUrl: exportUrl }),
        setSupportingDocsUrl: (url: string) => set({ supportingDocsUrl: url }),
        setSelectedOMVersion: (
            selectedOMVersion: FamilyIteration | undefined
        ) => set({ selectedOMVersion }),
        setOMDropdownOptions: (omDropdownOptions: FamilyIteration[] | []) =>
            set({ omDropdownOptions }),
        setElementList: (elementList: ProjectElementForImport[] | null) =>
            set({ elementList }),
        setAssemblyElementList: (
            elementList: ProjectElementForImport[] | null
        ) => set({ assemblyElementList: elementList }),
        setDivisionElementList: (
            elementList: ProjectElementForImport[] | null
        ) => set({ divisionElementList: elementList }),
        setSustainabilityElementList: (
            elementList: ProjectElementForImport[] | null
        ) => set({ sustainabilityElementList: elementList }),
    },
}))

/** State Selectors */

/** Current PDF export loading state */
export const useIsPDFExportLoading = () =>
    useActionBarStore((state) => state.isPDFExportLoading)

/** Current Word export loading state */
export const useIsWordExportLoading = () =>
    useActionBarStore((state) => state.isWordExportLoading)

/** Current supporting docs loading state */
export const useIsSupportingDocsLoading = () =>
    useActionBarStore((state) => state.isSupportingDocsLoading)

/** Current PDF export URL */
export const usePDFExportURL = () =>
    useActionBarStore((state) => state.pdfExportUrl)

/** Current Word export URL */
export const useWordExportURL = () =>
    useActionBarStore((state) => state.wordExportUrl)

/** Current supporting docs URL */
export const useSupportingDocsUrl = () =>
    useActionBarStore((state) => state.supportingDocsUrl)

/** Currently selected OM version */
export const useSelectedOMVersion = () =>
    useActionBarStore((state) => state.selectedOMVersion)

/** Available OM dropdown options */
export const useOMDropdownOptions = () =>
    useActionBarStore((state) => state.omDropdownOptions)

/** Default element list (backward compatibility) */
export const useElementList = () =>
    useActionBarStore((state) => state.elementList)

/** Assembly panel element list */
export const useAssemblyElementList = () =>
    useActionBarStore((state) => state.assemblyElementList)

/** Division panel element list */
export const useDivisionElementList = () =>
    useActionBarStore((state) => state.divisionElementList)

/** Sustainability panel element list */
export const useSustainabilityElementList = () =>
    useActionBarStore((state) => state.sustainabilityElementList)
/**
 * Hook to access action bar actions. Actions are stable between re-renders.
 * @returns All action bar actions for state management
 */
export const useActionBarActions = () =>
    useActionBarStore((state) => state.actions)

//  * @param selector - A function that takes the store state and returns the value to be selected.
//  * @returns The selected value from the store.

export const useShallowActionBarStoreSelector = <T>(
    selector: (state: ActionBarStoreType) => T
): T => useActionBarStore(useShallow(selector))
